import { AnalyticService } from '@application/analytic/analytic.service';
import { BadgeNumber, Flex, Icon, IconDictionary, VisuallyHidden } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { useAnalyticContext } from '@components/analytic_link/analytic_link.provider';
import { VisibaLink } from '@components/visiba-link.component';
import { withTranslation } from '@modules/i18n_and_l10n/with_translation';
import * as PrimitiveRadixDialog from '@radix-ui/react-dialog';
import { useUser } from '@services/auth/use_user';
import { useService } from '@visiba/instantiation';
import classNames from 'classnames';
import { Fragment, MouseEvent, ReactNode } from 'react';
import styled from 'styled-components';
import { useRouteDetails } from '../_use_route_details';

type ValidIcons = Extract<keyof IconDictionary, 'house' | 'medicalPlus' | 'archive' | 'user'>;

interface ListItemProps {
	to: string;
	children: ReactNode;
	authenticate?: boolean;
	icon: ValidIcons;
	badgeCount?: number;
	analyticQuirk__text?: string;
}

const StyledListItem = styled(VisibaLink)`
	min-height: 48px;
	padding: ${tokens.spacing.x1_5} ${tokens.spacing.x2};
	color: ${tokens.color.content.interactive};
	display: flex;
	align-items: center;
	width: 100%;
	border-radius: ${tokens.borderRadius.medium};
	transition:
		opacity 150ms ease-in-out,
		background-color 150ms ease-in-out;
	font: ${tokens.text.label.regular};
	gap: ${tokens.spacing.x1_5};

	&.isCurrentPage {
		font: ${tokens.text.label.semiBold};
		background-color: ${tokens.color.bg.interactiveMutedActive};
	}

	&.isDisabled {
		opacity: 0.7;
	}

	&:not(&.isDisabled) {
		@media (hover: hover) {
			&:hover {
				background-color: ${tokens.color.bg.interactiveMutedActive};
			}
		}
	}
`;

export function MenuListItem({
	badgeCount,
	icon,
	to: _to,
	authenticate,
	analyticQuirk__text = '',
	children,
}: ListItemProps): JSX.Element {
	const user = useUser();
	const [to, isCurrentPage] = useRouteDetails(_to);
	const isDisabled = authenticate && user == null;
	const analyticCategory = useAnalyticContext();
	const analyticService = useService(AnalyticService);

	function analyticQuirk_handleClick(): void {
		analyticService.trackEvent(analyticCategory, 'Clicked: ' + analyticQuirk__text);
	}

	function handleClick(event: MouseEvent<HTMLAnchorElement>): void {
		analyticQuirk_handleClick();
	}

	return (
		<li>
			<PrimitiveRadixDialog.Close asChild>
				<StyledListItem
					to={to}
					__TEMP__to={to}
					__TEMP__navigate={window.__TEMP__overrideNavigation}
					className={classNames({
						isCurrentPage,
						isDisabled,
					})}
					aria-disabled={isDisabled}
					onClick={handleClick}
				>
					<Icon size='small' type={isCurrentPage ? retrieveActiveIcon(icon) : icon} />
					<Flex grow={1}>{children}</Flex>

					{badgeCount != null ? (
						<BadgeNumber
							label={withTranslation(badgeCount === 1 ? 'C.Component.Bar.NewMessage.Singular' : 'C.Component.Bar.NewMessage.Plural', {
								number: `${badgeCount}`,
							})}
							value={badgeCount}
						/>
					) : null}

					{isDisabled ? (
						<Fragment>
							<Icon size='small' type='padLockKey' />
							<VisuallyHidden>{withTranslation('PW.Component.Header.NavigationMenu.Item.DisabledText')}</VisuallyHidden>
						</Fragment>
					) : null}
				</StyledListItem>
			</PrimitiveRadixDialog.Close>
		</li>
	);
}

function retrieveActiveIcon(icon: ValidIcons): keyof IconDictionary {
	switch (icon) {
		case 'house':
			return 'houseFilled';
		case 'medicalPlus':
			return 'medicalPlusFilled';
		case 'user':
			return 'userFilled';
		case 'archive':
			return 'archiveFilled';
		default:
			return 'appleLogo';
	}
}
